import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { NzMessageService } from "ng-zorro-antd/message";
import { HttpCommunicationService } from "../../../service/http-communication.service";
import { BulkNotificationMetricEnum, CommunicationTypeEnum, NotificationBulkEventsDto, NotificationBulkReportDto } from "../../../service/dto";
import {
	Chart,
	ChartConfiguration,
	ArcElement,
	DoughnutController,
	Tooltip,
	Legend
} from 'chart.js';
import { catchError } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { HttpFileService } from "../../../service/http-file.service";

// IMPORTANT: register controller + elements + plugins
Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

export interface Counts {
	total: number;
	success: number;
	failed: number;
	successPercentage: string;
	failedPercentage: string;
}

@Component({
	selector: 'app-communication-report',
	standalone: false,
	templateUrl: './communication-report.component.html',
	styleUrls: ['./communication-report.component.scss']
})
export class CommunicationReportComponent implements AfterViewInit {

	@ViewChild('deliveryChart') deliveryChart!: ElementRef<HTMLCanvasElement>;
	chart!: Chart;

	report!: NotificationBulkReportDto;
	events!: NotificationBulkEventsDto[];
	counts!: Counts;

	BulkNotificationMetricEnum = BulkNotificationMetricEnum;

	searchForm!: FormGroup;

	bulkLogId!: number;

	constructor(
		private message: NzMessageService,
		private httpService: HttpCommunicationService,
		private fb: FormBuilder,
		private route: ActivatedRoute,
		private fileService: HttpFileService,
	) {
		this.searchForm = this.fb.group({
			search: ['', Validators.required]
		});

		this.route.paramMap.subscribe(params => {
			const id = params.get('id');
			if (id) {
				this.bulkLogId = +id;
				this.fetchReport();
			} else {
				this.message.error("Invalid Communication");
			}
		});
		// this.report = {
		// 	name: 'Birth Date',
		// 	agent: {
		// 		hashCode: 1,
		// 		username: 'jcwouters',
		// 		firstName: 'JC',
		// 		lastName: 'Wouters',
		// 		status: 'Active',
		// 		superUser: true,
		// 		roles: []
		// 	} as unknown as AgentDto,
		// 	createdAt: new Date(),
		// 	sentAt: new Date(),
		// 	channel: 'Email',
		// 	metrics: {
		// 		[BulkNotificationMetricEnum.Sent]: '4',
		// 		[BulkNotificationMetricEnum.Delivered]: '3',
		// 		[BulkNotificationMetricEnum.Clicked]: '1',
		// 		[BulkNotificationMetricEnum.SpamComplaint]: '0',
		// 		[BulkNotificationMetricEnum.Rejected]: '0',
		// 		[BulkNotificationMetricEnum.TotalDeliveryTimeFirst]: '65787',
		// 	} as Record<BulkNotificationMetricEnum, string>,
		// 	attachments: [],
		// 	trigger: {
		// 		triggerName: 'Birth Date',
		// 		triggerDescription: 'Birthday Wishes',
		// 		triggerType: 'DateOfBirth',
		// 		frequency: "Annually",
		// 		reminderBefore: "Day",
		// 		reminderBeforeValue: 1,
		// 		triggerTime: '08:00',
		// 		description: 'Happy Birthday Wishes',
		// 	},
		// 	filters: [
		// 		{
		// 			propertyId: 353,
		// 			propertyName: 'Gender',
		// 			operator: FilterOperatorEnum.Equal,
		// 			dataType: "DropDownSingle",
		// 			value: 'Male',
		// 		},
		// 		{
		// 			propertyId: 353,
		// 			propertyName: 'Another Filter',
		// 			operator: FilterOperatorEnum.Equal,
		// 			dataType: "DropDownSingle",
		// 			value: 'Another Value',
		// 		},
		// 		{
		// 			propertyId: 353,
		// 			propertyName: 'Third Filter',
		// 			operator: FilterOperatorEnum.Equal,
		// 			dataType: "DropDownSingle",
		// 			value: 'Third Value',
		// 		}
		// 	],
		// 	subject: 'Happy Birthday!',
		// 	body: 'A very happy birthday to you!!',
		// 	recipients: [
		// 		{ name: 'Jane', surname: 'Dlamini', status: 'Delivered', sentAt: new Date(), error: '' },
		// 		{ name: 'Sipho', surname: 'Nkosi', status: 'Delivered', sentAt: new Date(), error: '' },
		// 		{ name: 'Lerato', surname: 'Mokoena', status: 'Delivered', sentAt: new Date(), error: '' }
		// 	],
		// } as NotificationBulkReportDto;

		// const total = Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Sent]));
		// const success = Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Delivered]));
		// const failed = total - Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Delivered]));

		// const successPct = ((success / total) * 100) + '%';
		// const failedPct = ((failed / total) * 100) + '%';

		// this.counts = {
		// 	total: total,
		// 	success: success,
		// 	failed: failed,
		// 	successPercentage: successPct,
		// 	failedPercentage: failedPct,
		// }
	}

	ngAfterViewInit(): void {
		// small safety: ensure DOM painted and CSS applied so Chart gets actual height
		setTimeout(() => this.initChart(), 0);
	}

	private fetchReport() {
		this.httpService.getReport(this.bulkLogId)
		.pipe(
			catchError(error => {
				console.log('Error fetching communication report', error);
				this.message.error('Failed to fetch communication report');
				throw error;
			})
		)
		.subscribe({
			next: (res) => {
				this.report = res;

				const total = Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Sent]));
				const success = Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Delivered]));
				const failed = total - Number.parseFloat(String(this.report.metrics[BulkNotificationMetricEnum.Delivered]));

				const successPct = ((success / total) * 100) + '%';
				const failedPct = ((failed / total) * 100) + '%';

				this.counts = {
					total: total,
					success: success,
					failed: failed,
					successPercentage: successPct,
					failedPercentage: failedPct,
				}
			},
			error: () => {
				
			},
		});
	}

	private initChart(): void {
		if (!this.deliveryChart || !this.deliveryChart.nativeElement) return;

		// Destroy previous chart if exists
		if (this.chart) {
			this.chart.destroy();
		}

		const config: ChartConfiguration<'doughnut'> = {
			type: 'doughnut',
			data: {
				labels: ['Delivered', 'Failed'],
				datasets: [
					{
						data: [this.counts.success, this.counts.failed],
						backgroundColor: [
							'#4CAF50', // Delivered
							'#F44336', // Failed
						],
						borderWidth: 0,
						hoverOffset: 6
					}
				]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false, // important for flexible container
				cutout: '70%',
				plugins: {
					legend: { display: false },
					tooltip: { enabled: true }
				}
			}
		};

		// Use the canvas element itself (not the 2D context) — this is simpler and robust
		const canvasEl = this.deliveryChart.nativeElement as HTMLCanvasElement;
		this.chart = new Chart(canvasEl, config);
	}

	getMetric(metric: BulkNotificationMetricEnum): string {
		if (metric == BulkNotificationMetricEnum.TotalDeliveryTimeFirst || metric == BulkNotificationMetricEnum.TotalDeliveryTimeSubsequent
			|| metric == BulkNotificationMetricEnum.AvgDeliveryTimeFirst || metric == BulkNotificationMetricEnum.AvgDeliveryTimeSubsequent) {
			return String(this.report.metrics[metric]) ? (Math.round(Number(this.report.metrics[metric]) / 1000)) + 's' : '0';
		}

		return String(this.report.metrics[metric]) ?? 0;
	}

	getPositiveMetrics(): any[] {
		return Object.entries(this.report.metrics) 
			.filter(([_, value]) => Number(value) > 0) 
			.map(([key, _]) => {
				const metricEnum = key as BulkNotificationMetricEnum;
				const formattedName = metricEnum.replace(/([A-Z])/g, ' $1').trim();
				return `${formattedName}: ${this.getMetric(metricEnum)}`;
			});
	}

	downloadAttachments() {
		this.report.attachments.forEach(item => {
			this.fileService.downloadFile(item.uuid).subscribe();
		});		
	}

	doSearch() {

		var search = this.searchForm.get('search')!.value;
		if (!search) {
			this.message.error("Please enter search criteria");
			return;
		}

		this.httpService.searchReport(this.bulkLogId, search).subscribe(result => {
			this.events = result;
			if (!this.events || this.events.length === 0) {
				this.message.error(`No results found for ${search}`)
			}
		});
	}

	getDeliveryToOpenDuration(input: NotificationBulkEventsDto): string | undefined {
        if (!input.deliveryAt || !input.openedAt?.length) return undefined;

        const firstOpen = new Date(input.openedAt[input.openedAt.length - 1]);
        const diffMs = firstOpen.getTime() - new Date(input.deliveryAt).getTime();

        if (diffMs < 0) return undefined;

        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;

		var out = '';
		if (days > 0) {
			out = `${days} dy `
		}
		out += `${hours} hr `
		out += `${minutes} mi`

        return out;
    }

}
